import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Ruler, Sparkles, MessageCircle, Check } from "lucide-react";
import { Seo } from "@/components/Seo";
import { TryOnModal } from "@/components/TryOnModal";
import { api, money, cdnImg } from "@/lib/api";
import { toast } from "sonner";

const FUNCTIONS = [
  { name: "haldi", label: "Haldi", hint: "yellows & sunshine tones", bg: "#F5C518" },
  { name: "mehandi", label: "Mehandi", hint: "greens & festive hues", bg: "#2F5D3A" },
  { name: "sangeet", label: "Sangeet", hint: "glam, shimmer & dance-ready", bg: "#7B2D43" },
  { name: "wedding", label: "Wedding", hint: "rich silks & statement pieces", bg: "#B84A3B" },
  { name: "reception", label: "Reception", hint: "gowns & evening elegance", bg: "#1A1A1A" },
];

const STEPS = ["Functions", "Your vibe", "Measurements", "Lookbook", "Results"];

const US_SIZES = ["XXS", "XS", "S", "M", "L", "XL", "2XL", "3XL"];
const US_MAP_HINT = "XXS=36 · XS=38 · S=40 · M=42 · L=44 · XL=46 · 2XL=48 · 3XL=50";

const inputCls = "w-full border border-[#E5E5DF] px-4 py-3 text-sm focus:outline-none focus:border-[#B84A3B] transition-colors bg-[#FFFFFF]";

export default function OutfitFinder() {
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [meas, setMeas] = useState({ chest: "", waist: "", hip: "" });
  const [usualUs, setUsualUs] = useState("");
  const [contact, setContact] = useState({ email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [tryOnProduct, setTryOnProduct] = useState(null);

  const toggleFn = (name) =>
    setSelected((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

  const setPref = (name, key, val) =>
    setPrefs((prev) => ({ ...prev, [name]: { color: "", style: "", ...prev[name], [key]: val } }));

  const validMeas =
    [meas.chest, meas.waist, meas.hip].every((v) => v !== "" && Number(v) >= 24 && Number(v) <= 70);
  const canProceedSizing = validMeas || !!usualUs;

  const runFinder = async (withContact) => {
    setLoading(true);
    try {
      const { data } = await api.post("/outfit-finder", {
        functions: selected.map((n) => ({ name: n, color: prefs[n]?.color || "", style: prefs[n]?.style || "" })),
        chest: Number(meas.chest) || 0,
        waist: Number(meas.waist) || 0,
        hip: Number(meas.hip) || 0,
        usual_us: usualUs,
        email: withContact ? contact.email : "",
        phone: withContact ? contact.phone : "",
      });
      setResult(data);
      setStep(4);
      localStorage.setItem("kc_my_size", JSON.stringify({ garment: data.size_garment, us: data.size_us }));
      if (withContact) {
        toast.success(contact.email
          ? "Saved! Your lookbook is on its way to your inbox."
          : "Saved! We'll text you your lookbook shortly.");
      }
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setLoading(false);
    }
  };

  const canNext = step === 0 ? selected.length > 0 : step === 2 ? canProceedSizing : true;

  return (
    <div className="min-h-[70vh] bg-[#FAFAF7]" data-testid="outfit-finder">
      <Seo
        title="Wedding Guest Outfit Finder & Right Size Finder"
        description="Attending Haldi, Mehandi, Sangeet, a wedding or reception? Answer 3 quick questions and get outfit picks matched to the function, your colors and your perfect size."
        path="/outfit-finder"
      />
      <div className="max-w-[760px] mx-auto px-5 py-12 sm:py-16">
        <div className="text-center mb-10">
          <div className="overline text-[#B84A3B]">Free styling tool · 60 seconds</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-2">Find Your Perfect Outfit</h1>
        </div>

        {step < 4 && (
          <div className="flex items-center justify-center gap-2 mb-10" data-testid="of-progress">
            {STEPS.slice(0, 4).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[0.65rem] font-mono transition-colors duration-300 ${
                  i < step ? "bg-[#2F5D3A] text-white" : i === step ? "bg-[#B84A3B] text-white" : "bg-[#E5E5DF] text-[#6E6E69]"
                }`}>
                  {i < step ? <Check size={13} /> : i + 1}
                </span>
                {i < 3 && <span className={`w-8 h-px ${i < step ? "bg-[#2F5D3A]" : "bg-[#E5E5DF]"}`} />}
              </div>
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {step === 0 && (
              <div>
                <h2 className="font-display text-2xl text-center mb-1.5">Which functions are you attending?</h2>
                <p className="text-sm text-[#6E6E69] text-center mb-8">Pick as many as you like.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {FUNCTIONS.map((f) => {
                    const on = selected.includes(f.name);
                    return (
                      <button
                        key={f.name}
                        data-testid={`of-func-${f.name}`}
                        onClick={() => toggleFn(f.name)}
                        className={`border p-4 text-left transition-all duration-300 ${
                          on ? "border-[#B84A3B] bg-[#B84A3B]/5 shadow-sm" : "border-[#E5E5DF] bg-[#FFFFFF] hover:border-[#B84A3B]/50"
                        }`}
                      >
                        <span className="w-3 h-3 rounded-full inline-block mb-2" style={{ background: f.bg }} />
                        <span className="block font-display text-lg">{f.label}</span>
                        <span className="block text-[0.68rem] font-mono text-[#6E6E69] mt-0.5">{f.hint}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 className="font-display text-2xl text-center mb-1.5">Any color theme or style in mind?</h2>
                <p className="text-sm text-[#6E6E69] text-center mb-8">Totally optional — skip anything you&apos;re unsure about.</p>
                <div className="space-y-4">
                  {selected.map((name) => {
                    const f = FUNCTIONS.find((x) => x.name === name);
                    return (
                      <div key={name} className="border border-[#E5E5DF] bg-[#FFFFFF] p-5" data-testid={`of-pref-${name}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: f.bg }} />
                          <span className="font-display text-lg">{f.label}</span>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <input
                            data-testid={`of-color-${name}`}
                            value={prefs[name]?.color || ""}
                            onChange={(e) => setPref(name, "color", e.target.value)}
                            placeholder={name === "haldi" ? "Color theme (e.g. yellow)" : "Color theme (optional)"}
                            className={inputCls}
                          />
                          <input
                            data-testid={`of-style-${name}`}
                            value={prefs[name]?.style || ""}
                            onChange={(e) => setPref(name, "style", e.target.value)}
                            placeholder="Dress style (e.g. anarkali, saree)"
                            className={inputCls}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="font-display text-2xl text-center mb-1.5 flex items-center justify-center gap-2">
                  <Ruler size={20} className="text-[#B84A3B]" /> Let&apos;s find your right size
                </h2>
                <p className="text-sm text-[#6E6E69] text-center mb-8 max-w-md mx-auto">
                  Three quick measurements in inches. We size you the boutique way — comfort ease included.
                  No tape measure? Just pick your usual US size below.
                </p>
                <div className="grid sm:grid-cols-3 gap-4 max-w-lg mx-auto">
                  {[["chest", "Chest", "fullest part of bust"], ["waist", "Waist", "natural waistline"], ["hip", "Hip", "fullest part of hip"]].map(([k, label, hint]) => (
                    <div key={k}>
                      <label className="overline text-[#1A1A1A] block mb-1.5">{label}</label>
                      <div className="relative">
                        <input
                          data-testid={`of-${k}`}
                          type="number"
                          min="24"
                          max="70"
                          value={meas[k]}
                          onChange={(e) => setMeas((m) => ({ ...m, [k]: e.target.value }))}
                          placeholder="0"
                          className={`${inputCls} pr-10`}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[0.65rem] font-mono text-[#6E6E69]">in</span>
                      </div>
                      <p className="text-[0.62rem] font-mono text-[#6E6E69] mt-1">{hint}</p>
                    </div>
                  ))}
                </div>
                <div className="max-w-lg mx-auto mt-8 text-center">
                  <div className="overline text-[#6E6E69] mb-2.5">Or your usual US size in regular tops</div>
                  <div className="flex flex-wrap justify-center gap-2" data-testid="of-us-sizes">
                    {US_SIZES.map((u) => (
                      <button
                        key={u}
                        type="button"
                        data-testid={`of-us-${u}`}
                        onClick={() => setUsualUs((cur) => (cur === u ? "" : u))}
                        className={`min-w-[2.8rem] px-3 h-10 border text-[0.72rem] font-mono transition-colors ${
                          usualUs === u
                            ? "bg-[#B84A3B] text-[#FFFFFF] border-[#B84A3B]"
                            : "border-[#E5E5DF] bg-[#FFFFFF] hover:border-[#1A1A1A]"
                        }`}
                      >
                        {u}
                      </button>
                    ))}
                  </div>
                  <p className="text-[0.6rem] font-mono text-[#B0B0A8] mt-2">{US_MAP_HINT}</p>
                  {usualUs && validMeas && (
                    <p className="text-[0.62rem] font-mono text-[#6E6E69] mt-1.5">We&apos;ll use whichever gives the comfier fit.</p>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="font-display text-2xl text-center mb-1.5 flex items-center justify-center gap-2">
                  <MessageCircle size={20} className="text-[#1FA855]" /> Get your lookbook
                </h2>
                <p className="text-sm text-[#6E6E69] text-center mb-8 max-w-md mx-auto">
                  Enter your email and WhatsApp number — we&apos;ll text you your outfit suggestions so you have them handy while shopping.
                </p>
                <div className="max-w-sm mx-auto space-y-3">
                  <input
                    data-testid="of-email"
                    type="email"
                    value={contact.email}
                    onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                    placeholder="Email address"
                    className={inputCls}
                  />
                  <div className="flex border border-[#E5E5DF] focus-within:border-[#B84A3B] transition-colors bg-[#FFFFFF]">
                    <span className="px-3 py-3 text-sm font-mono text-[#6E6E69] bg-[#EFEFEA] border-r border-[#E5E5DF]">+1</span>
                    <input
                      data-testid="of-phone"
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                      placeholder="WhatsApp number"
                      className="flex-1 px-4 py-3 text-sm focus:outline-none"
                    />
                  </div>
                  <button
                    data-testid="of-submit"
                    onClick={() => runFinder(true)}
                    disabled={loading || (!contact.email && !contact.phone)}
                    className="w-full bg-[#1A1A1A] text-[#FFFFFF] py-4 text-[0.72rem] font-mono tracking-[0.22em] uppercase hover:bg-[#B84A3B] transition-colors duration-300 disabled:opacity-50"
                  >
                    {loading ? "Styling you…" : "Text Me My Lookbook"}
                  </button>
                  <button
                    data-testid="of-skip"
                    onClick={() => runFinder(false)}
                    disabled={loading}
                    className="w-full text-center text-[0.65rem] font-mono tracking-wide text-[#6E6E69] hover:text-[#1A1A1A] transition-colors py-1"
                  >
                    Skip — just show my picks
                  </button>
                </div>
              </div>
            )}

            {step === 4 && result && (
              <div data-testid="of-results">
                <div className="bg-[#1A1A1A] text-[#FFFFFF] p-6 sm:p-8 text-center mb-10" data-testid="of-size-card">
                  <div className="overline text-[#B84A3B]">Your boutique size</div>
                  <div className="font-display text-5xl mt-2" data-testid="of-size">
                    Size {result.size_garment} <span className="text-2xl text-[#B0B0A8]">· US {result.size_us}</span>
                  </div>
                  {result.advice.length > 0 && (
                    <ul className="mt-4 space-y-1.5 max-w-md mx-auto text-left" data-testid="of-advice">
                      {result.advice.map((a, i) => (
                        <li key={i} className="text-[0.8rem] font-body text-[#DDDDD5] flex gap-2">
                          <Sparkles size={13} className="text-[#B84A3B] shrink-0 mt-0.5" /> {a}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex items-center gap-3 border border-[#B84A3B]/30 bg-[#B84A3B]/5 px-4 py-3 mb-8" data-testid="of-tryon-note">
                  <Sparkles size={16} className="text-[#B84A3B] shrink-0" />
                  <p className="text-[0.8rem] font-body text-[#1A1A1A]">
                    See it on you before you buy — tap <b>Try It On</b> under any pick for a virtual preview, then order when you love it.
                  </p>
                </div>

                {result.results.map((r) => (
                  <div key={r.function} className="mb-10" data-testid={`of-result-${r.function.toLowerCase()}`}>
                    <div className="flex items-baseline gap-3 mb-4">
                      <h3 className="font-display text-2xl">{r.function}</h3>
                      <span className="text-[0.68rem] font-mono text-[#6E6E69]">
                        {r.products.length} handpicked options
                        {(r.color || r.style) ? ` · ${[r.color, r.style].filter(Boolean).join(" · ")}` : ""}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {r.products.map((p) => (
                        <div key={p.slug} className="group bg-[#FFFFFF] border border-[#E5E5DF] overflow-hidden flex flex-col">
                          <Link to={`/product/${p.slug}`} data-testid={`of-product-${p.slug}`} className="flex-1">
                            <div className="aspect-[2/3] overflow-hidden bg-[#EFEFEA]">
                              {p.image && (
                                <img src={cdnImg(p.image, 500)} alt={p.title} loading="lazy"
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                              )}
                            </div>
                            <div className="p-3 pb-1.5">
                              <p className="text-[0.78rem] font-body leading-snug line-clamp-2 group-hover:text-[#B84A3B] transition-colors">{p.title}</p>
                              <p className="text-sm font-semibold mt-1">
                                {money(p.price)}
                                {p.compare_at > p.price && (
                                  <span className="text-[0.68rem] text-[#B0B0A8] line-through ml-1.5 font-normal">{money(p.compare_at)}</span>
                                )}
                              </p>
                            </div>
                          </Link>
                          <button
                            data-testid={`of-tryon-${p.slug}`}
                            onClick={() => setTryOnProduct({ slug: p.slug, title: p.title, price: p.price, images: [p.image] })}
                            className="m-3 mt-1.5 flex items-center justify-center gap-1.5 border border-[#1A1A1A] py-2 text-[0.6rem] font-mono tracking-[0.16em] uppercase hover:bg-[#1A1A1A] hover:text-[#FFFFFF] transition-colors"
                          >
                            <Sparkles size={11} className="text-[#B84A3B]" /> Try It On
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="text-center mt-6">
                  <button data-testid="of-restart" onClick={() => { setStep(0); setResult(null); window.scrollTo(0, 0); }}
                    className="text-[0.68rem] font-mono tracking-[0.15em] uppercase border-b border-[#1A1A1A] pb-1 hover:text-[#B84A3B] hover:border-[#B84A3B] transition-colors">
                    Start over
                  </button>
                </div>

                <TryOnModal
                  open={!!tryOnProduct}
                  onOpenChange={() => setTryOnProduct(null)}
                  product={tryOnProduct || {}}
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {step < 4 && step !== 3 && (
          <div className="flex items-center justify-between mt-10 max-w-lg mx-auto">
            <button
              data-testid="of-back"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="flex items-center gap-1.5 text-[0.68rem] font-mono tracking-[0.15em] uppercase text-[#6E6E69] hover:text-[#1A1A1A] transition-colors disabled:opacity-0"
            >
              <ArrowLeft size={13} /> Back
            </button>
            <button
              data-testid="of-next"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext}
              className="flex items-center gap-2 bg-[#1A1A1A] text-[#FFFFFF] px-7 py-3.5 text-[0.68rem] font-mono tracking-[0.2em] uppercase hover:bg-[#B84A3B] transition-colors duration-300 disabled:opacity-40"
            >
              {step === 2 ? "Almost there" : "Continue"} <ArrowRight size={13} />
            </button>
          </div>
        )}
        {step === 3 && (
          <div className="mt-6 text-center">
            <button data-testid="of-back-3" onClick={() => setStep(2)}
              className="text-[0.68rem] font-mono tracking-[0.15em] uppercase text-[#6E6E69] hover:text-[#1A1A1A] transition-colors">
              <ArrowLeft size={12} className="inline mr-1" /> Back to measurements
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
